import './Input.sass';

import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import copyToClipboard from 'copy-text-to-clipboard';

import { InputProps } from './Input.interface';
import { classNames as cn } from '$utils';
import { IcCopy20, IcHide20, IcView20 } from '$assets';
import { toastActions } from '$store/toast';
import { useTranslator } from '$hooks';
import { CurrencyEnum } from '$store/models';

const INPUT_HEIGHT = 48;
const TEXTAREA_HEIGHT = 96;

// eslint-disable-next-line react/display-name
export default forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  const {
    multiline = false,
    lineHeight = 24,
    padding = 12,
    maxLines = 8,
    minHeight,
    autoFocus,
    style,
    type,
    currency,
    currencyAlign = 'start',
    minPrice,
    maxPrice,
    ...otherProps
  } = props;

  const minHeightRes = minHeight || (multiline ? TEXTAREA_HEIGHT : INPUT_HEIGHT);

  const input = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const [height, setHeight] = useState(minHeightRes);
  const [visiblePassword, setVisiblePassword] = useState(false);
  const [isFocused, setFocused] = useState(false);
  const dispatch = useDispatch();
  const t = useTranslator();

  const handleFocus = useCallback(() => {
    setFocused(true);
  }, []);

  const handleCopy = useCallback(() => {
    if (otherProps.value) {
      copyToClipboard(otherProps.value);
      dispatch(toastActions.setToastSuccess(t('copied')));
    }
  }, [dispatch, otherProps.value, t]);

  const handleBlur = useCallback(() => {
    setFocused(false);
  }, []);

  const inputType =
    type === 'copy'
      ? 'text'
      : type === 'password' && visiblePassword
      ? 'text'
      : type === 'price'
      ? 'number'
      : type;

  let preparedProps: React.InputHTMLAttributes<HTMLInputElement> &
    React.TextareaHTMLAttributes<HTMLTextAreaElement> = {
    onFocus: handleFocus,
    onBlur: handleBlur,
    ...otherProps,
    autoFocus,
    type: inputType,
    className: cn('Input', {
      focused: isFocused,
    }),
    style: {
      height,
      minHeight: minHeightRes,
      lineHeight: `${lineHeight}px`,
      paddingTop: padding,
      paddingBottom: padding,
      paddingLeft: currency && currencyAlign === 'start' ? 8 : 16,
      ...style,
    },
  };

  if (type === 'price') {
    const step =
      currency === CurrencyEnum.PLACE
        ? '1'
        : currency === CurrencyEnum.TON
        ? '0.001'
        : '0.01';
    preparedProps = {
      ...preparedProps,
      min: minPrice,
      max: maxPrice,
      step,
      inputMode: 'decimal',
    };
  }

  /* @ts-ignore */
  useImperativeHandle(ref, () => ({
    focus() {
      /* @ts-ignore */
      input.current && input.current.focus();
    },
    resetHeight() {
      setHeight(minHeightRes);
    },
  }));

  useEffect(() => {
    if (!input.current) {
      return;
    }

    const el = input.current as HTMLTextAreaElement;

    el.style.height = '0px';

    const newHeight = Math.min(
      Math.max(minHeightRes, el.scrollHeight),
      lineHeight * maxLines + padding * 2,
    );

    el.style.height = `${newHeight}px`;
    setHeight(newHeight);
  }, [props.value, input.current]);

  const handleOnChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (type === 'price') {
        const raw = event.target.value;

        if (raw === '') {
          if (otherProps.onChange) {
            otherProps.onChange(event as any);
          }
          return;
        }

        const num = Number(raw.replace(',', '.'));

        if (!Number.isNaN(num)) {
          if (typeof maxPrice === 'number' && num > maxPrice) {
            if (input.current) {
              input.current.value = String(maxPrice);
            }
            return;
          }

          if (typeof minPrice === 'number' && num < minPrice) {
            if (input.current) {
              input.current.value = String(minPrice);
            }
            return;
          }
        }
      }

      if (otherProps.onChange) {
        otherProps.onChange(event as any);
      }
    },
    [type, maxPrice, minPrice, otherProps, props.value],
  );

  preparedProps.onChange = handleOnChange;

  const currencySign = useMemo(() => {
    return currency ? (
      <div className={cn('Input__currency', { right: currencyAlign === 'end' })}>
        {currency === CurrencyEnum.EUR ? '€' : currency.toUpperCase()}
      </div>
    ) : null;
  }, [currency, currencyAlign]);

  return (
    <div
      onClick={type === 'copy' ? handleCopy : undefined}
      className={cn('Input__wrapper', { copy: type === 'copy' })}
    >
      {type === 'price' && currencyAlign === 'start' && currencySign}

      {/* @ts-ignore */}
      {multiline ? (
        <textarea
          {...(preparedProps as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
          ref={input as React.RefObject<HTMLTextAreaElement>}
          onChange={handleOnChange}
        />
      ) : (
        <input
          {...(preparedProps as React.InputHTMLAttributes<HTMLInputElement>)}
          ref={input as React.RefObject<HTMLInputElement>}
          onChange={handleOnChange}
        />
      )}
      {type === 'price' && currencyAlign === 'end' && currencySign}

      {type === 'password' && (
        <div
          onClick={() => setVisiblePassword(!visiblePassword)}
          className="Input__button"
        >
          {visiblePassword ? <IcHide20 /> : <IcView20 />}
        </div>
      )}

      {type === 'copy' && (
        <div className="Input__button">
          <IcCopy20 />
        </div>
      )}
    </div>
  );
});
